import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  useInView,
  AnimatePresence,
} from 'framer-motion';
import {
  Calendar,
  Video,
  Ticket,
  BookOpen,
  Clock,
  Settings,
  ArrowRight,
  Users,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Image as ImageIcon,
  BarChart3,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: '#C9A84C',
  confirmed: '#22c55e',
  cancelled: '#ef4444',
};

const STATUS_GRADIENTS = {
  pending: ['#FFD97A', '#C9A84C'],
  confirmed: ['#4ade80', '#16a34a'],
  cancelled: ['#f87171', '#dc2626'],
};

/* ---------- Count-up number ---------- */
const CountUp = ({ value, duration = 1.4, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => Math.round(v).toLocaleString('fr-FR'));

  useEffect(() => {
    if (inView) {
      const controls = animate(mv, value, {
        duration,
        ease: [0.22, 1, 0.36, 1],
      });
      return controls.stop;
    }
  }, [inView, value, duration, mv]);

  return (
    <motion.span ref={ref} className={className}>
      {display}
    </motion.span>
  );
};

/* ---------- Sparkline ---------- */
const Sparkline = ({ data, color = '#C9A84C', gradId }) => (
  <div className="h-10 w-full -mx-1">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={1} />
          </linearGradient>
        </defs>
        <Line
          type="monotone"
          dataKey="v"
          stroke={`url(#${gradId})`}
          strokeWidth={2}
          dot={false}
          isAnimationActive={true}
          animationDuration={1200}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

/* ---------- Skeleton ---------- */
const SkeletonCard = ({ className = '' }) => (
  <div className={`bg-white/5 border border-white/10 rounded-2xl p-5 overflow-hidden relative ${className}`}>
    <div className="animate-pulse space-y-3">
      <div className="w-10 h-10 rounded-xl bg-white/10" />
      <div className="h-8 bg-white/10 rounded w-1/2" />
      <div className="h-3 bg-white/10 rounded w-2/3" />
      <div className="h-10 bg-white/5 rounded mt-3" />
    </div>
  </div>
);

/* ---------- Helpers ---------- */
const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30';
    case 'confirmed':
      return 'bg-green-500/15 text-green-400 border-green-500/30';
    case 'cancelled':
      return 'bg-red-500/15 text-red-400 border-red-500/30';
    default:
      return 'bg-white/10 text-[#B9B2A8] border-white/20';
  }
};

const getStatusDot = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]';
    case 'confirmed':
      return 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.6)]';
    case 'cancelled':
      return 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.6)]';
    default:
      return 'bg-[#B9B2A8]';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'pending':
      return 'En attente';
    case 'confirmed':
      return 'Confirmée';
    case 'cancelled':
      return 'Annulée';
    default:
      return status;
  }
};

/* ---------- Pie custom label ---------- */
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (!percent || percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 12, fontWeight: 700 }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ---------- Main component ---------- */
const AdminDashboardPage = () => {
  const [stats, setStats] = useState({
    events: 0,
    videos: 0,
    bookings: 0,
    timeline: 0,
  });
  const [bookingsThisMonth, setBookingsThisMonth] = useState(0);
  const [bookingsByStatus, setBookingsByStatus] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [weeklyBookings, setWeeklyBookings] = useState([]);
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem('admin_auth');
    const headers = { Authorization: `Basic ${auth}` };

    const buildWeeklyFromBookings = (allBookings) => {
      const days = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        d.setHours(0, 0, 0, 0);
        const next = new Date(d);
        next.setDate(d.getDate() + 1);
        const count = allBookings.filter((b) => {
          const bd = new Date(b.created_at);
          return bd >= d && bd < next;
        }).length;
        days.push({
          day: d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', ''),
          count,
        });
      }
      return days;
    };

    const buildSparkline = (total, variance = 0.25) => {
      // generate a gentle rising line ending at total (deterministic-ish)
      const points = 8;
      const base = Math.max(1, total);
      const arr = [];
      for (let i = 0; i < points; i++) {
        const p = i / (points - 1);
        const noise = (Math.sin(i * 1.7) + 1) / 2;
        arr.push({ v: base * (p * 0.7 + 0.3) * (1 - variance / 2 + noise * variance) });
      }
      arr[points - 1] = { v: base };
      return arr;
    };

    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/analytics`, { headers });
        const data = response.data;

        setStats({
          events: data.total_events || 0,
          videos: data.total_videos || 0,
          bookings: data.total_bookings || 0,
          timeline: data.events_upcoming || 0,
        });
        setBookingsThisMonth(data.bookings_this_month || 0);

        const statusData = data.bookings_by_status || {};
        const pieData = Object.entries(statusData)
          .filter(([, value]) => value > 0)
          .map(([name, value]) => ({ name, value }));
        setBookingsByStatus(pieData);

        const recent = data.recent_bookings || [];
        setRecentBookings(recent);

        // Weekly chart - prefer server data if available
        if (Array.isArray(data.bookings_per_day) && data.bookings_per_day.length) {
          setWeeklyBookings(
            data.bookings_per_day.map((d) => ({
              day: d.day || d.label || '',
              count: d.count || d.value || 0,
            }))
          );
        } else {
          setWeeklyBookings(buildWeeklyFromBookings(recent));
        }

        setAnalyticsLoaded(true);
      } catch (error) {
        console.error('Error fetching analytics, falling back:', error);
        fetchFallbackData(headers);
      }
    };

    const fetchFallbackData = async (hdrs) => {
      try {
        const [eventsRes, videosRes, bookingsRes, timelineRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/events`, { headers: hdrs }),
          axios.get(`${BACKEND_URL}/api/admin/videos`, { headers: hdrs }),
          axios.get(`${BACKEND_URL}/api/admin/bookings`, { headers: hdrs }),
          axios.get(`${BACKEND_URL}/api/admin/timeline`, { headers: hdrs }),
        ]);

        const allBookings = bookingsRes.data.bookings || [];
        const now = new Date();
        const thisMonthBookings = allBookings.filter((b) => {
          const d = new Date(b.created_at);
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });

        const statusCounts = {};
        allBookings.forEach((b) => {
          statusCounts[b.status] = (statusCounts[b.status] || 0) + 1;
        });
        const pieData = Object.entries(statusCounts)
          .filter(([, value]) => value > 0)
          .map(([name, value]) => ({ name, value }));

        setStats({
          events: eventsRes.data.events?.length || 0,
          videos: videosRes.data.videos?.length || 0,
          bookings: allBookings.length,
          timeline: timelineRes.data.timeline?.length || 0,
        });
        setBookingsThisMonth(thisMonthBookings.length);
        setBookingsByStatus(pieData);
        setRecentBookings(allBookings.slice(0, 5));
        setWeeklyBookings(buildWeeklyFromBookings(allBookings));
        setAnalyticsLoaded(true);
      } catch (error) {
        console.error('Error fetching fallback stats:', error);
        setAnalyticsLoaded(true);
      }
    };

    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todayString = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const statCards = [
    {
      icon: Calendar,
      label: 'Événements',
      value: stats.events,
      path: '/admin/events',
      color: '#3b82f6',
      gradient: 'from-blue-500 to-blue-600',
      ring: 'ring-blue-500/20',
      trend: 12,
      sparkData: (() => {
        const n = Math.max(1, stats.events);
        return [
          { v: n * 0.3 },
          { v: n * 0.5 },
          { v: n * 0.45 },
          { v: n * 0.7 },
          { v: n * 0.8 },
          { v: n * 0.75 },
          { v: n * 0.95 },
          { v: n },
        ];
      })(),
    },
    {
      icon: Video,
      label: 'Vidéos',
      value: stats.videos,
      path: '/admin/videos',
      color: '#a855f7',
      gradient: 'from-purple-500 to-purple-600',
      ring: 'ring-purple-500/20',
      trend: 8,
      sparkData: (() => {
        const n = Math.max(1, stats.videos);
        return [
          { v: n * 0.4 },
          { v: n * 0.55 },
          { v: n * 0.6 },
          { v: n * 0.58 },
          { v: n * 0.75 },
          { v: n * 0.82 },
          { v: n * 0.9 },
          { v: n },
        ];
      })(),
    },
    {
      icon: BookOpen,
      label: 'Réservations',
      value: stats.bookings,
      path: '/admin/bookings',
      color: '#22c55e',
      gradient: 'from-green-500 to-green-600',
      ring: 'ring-green-500/20',
      trend: 23,
      sparkData: (() => {
        const n = Math.max(1, stats.bookings);
        return [
          { v: n * 0.2 },
          { v: n * 0.35 },
          { v: n * 0.5 },
          { v: n * 0.6 },
          { v: n * 0.7 },
          { v: n * 0.85 },
          { v: n * 0.92 },
          { v: n },
        ];
      })(),
    },
    {
      icon: Users,
      label: 'Ce mois',
      value: bookingsThisMonth,
      path: '/admin/bookings',
      color: '#C9A84C',
      gradient: 'from-[#C9A84C] to-[#a3873e]',
      ring: 'ring-[#C9A84C]/20',
      trend: bookingsThisMonth > 0 ? 15 : -5,
      sparkData: (() => {
        const n = Math.max(1, bookingsThisMonth);
        return [
          { v: n * 0.5 },
          { v: n * 0.45 },
          { v: n * 0.6 },
          { v: n * 0.75 },
          { v: n * 0.7 },
          { v: n * 0.85 },
          { v: n * 0.95 },
          { v: n },
        ];
      })(),
    },
  ];

  const quickActions = [
    {
      icon: ImageIcon,
      title: 'Modifier la page d\'accueil',
      desc: 'Vidéo, logo, liens',
      path: '/admin/hero',
      color: '#C9A84C',
      grad: 'from-[#C9A84C]/30 to-[#C9A84C]/10',
    },
    {
      icon: Calendar,
      title: 'Gérer les événements',
      desc: 'Ajouter, modifier, supprimer',
      path: '/admin/events',
      color: '#3b82f6',
      grad: 'from-blue-500/30 to-blue-500/10',
    },
    {
      icon: Ticket,
      title: 'Gérer les tickets',
      desc: 'Modifier les prix et statuts',
      path: '/admin/tickets',
      color: '#22c55e',
      grad: 'from-green-500/30 to-green-500/10',
    },
    {
      icon: Video,
      title: 'Gérer les vidéos',
      desc: 'YouTube, Instagram',
      path: '/admin/videos',
      color: '#a855f7',
      grad: 'from-purple-500/30 to-purple-500/10',
    },
    {
      icon: BookOpen,
      title: 'Gérer la galerie',
      desc: 'Photos et albums',
      path: '/admin/gallery',
      color: '#ec4899',
      grad: 'from-pink-500/30 to-pink-500/10',
    },
    {
      icon: Settings,
      title: 'Paramètres',
      desc: 'Countdown, configuration',
      path: '/admin/settings',
      color: '#f97316',
      grad: 'from-orange-500/30 to-orange-500/10',
    },
  ];

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0];
      return (
        <div className="bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 rounded-lg px-3 py-2 text-sm shadow-xl">
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: STATUS_COLORS[name] || '#B9B2A8' }}
            />
            <span className="text-[#F5F0E8]">{getStatusLabel(name)}</span>
            <span className="text-[#C9A84C] font-bold ml-2">{value}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 rounded-lg px-3 py-2 text-sm shadow-xl">
          <div className="text-[#B9B2A8] text-xs uppercase mb-0.5">{label}</div>
          <div className="text-[#F5F0E8] font-bold">
            {payload[0].value} <span className="text-[#B9B2A8] font-normal text-xs">réservation(s)</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.05 },
    },
  };
  const item = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <motion.div
      data-testid="admin-dashboard-page"
      variants={container}
      initial="hidden"
      animate="show"
    >
      {/* Welcome header */}
      <motion.div variants={item} className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#C9A84C]" />
            <h1 className="font-display text-3xl text-[#F5F0E8] tracking-tight">
              Bonjour, <span className="text-[#C9A84C]">Admin</span>
            </h1>
          </div>
          <p className="text-sm text-[#B9B2A8] capitalize">{todayString}</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs text-[#B9B2A8]">Système en ligne</span>
        </div>
      </motion.div>

      {/* Stats grid */}
      {!analyticsLoaded ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <motion.div
          variants={item}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              variants={item}
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <Link
                to={stat.path}
                className="block relative bg-white/5 hover:bg-white/[0.07] border border-white/10 hover:border-white/20 rounded-2xl p-5 transition-all group overflow-hidden"
                style={{
                  boxShadow: '0 4px 20px -8px rgba(0,0,0,0.5)',
                }}
              >
                {/* Glow on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: `radial-gradient(circle at 30% 0%, ${stat.color}22, transparent 70%)`,
                  }}
                />

                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}
                      style={{
                        boxShadow: `0 4px 16px -4px ${stat.color}66`,
                      }}
                    >
                      <stat.icon className="w-5 h-5 text-white" />
                    </div>
                    <div
                      className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
                        stat.trend >= 0
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-red-500/15 text-red-400'
                      }`}
                    >
                      {stat.trend >= 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {stat.trend >= 0 ? '+' : ''}
                      {stat.trend}%
                    </div>
                  </div>

                  <div className="font-display text-3xl text-[#F5F0E8] mb-1 tabular-nums">
                    <CountUp value={stat.value} />
                  </div>
                  <div className="text-[11px] text-[#B9B2A8] uppercase tracking-[0.15em] font-medium">
                    {stat.label}
                  </div>

                  {/* Sparkline */}
                  <div className="mt-3">
                    <Sparkline
                      data={stat.sparkData}
                      color={stat.color}
                      gradId={`spark-${index}`}
                    />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Charts row: Pie + Bar */}
      <motion.div variants={item} className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
        {/* Pie Chart */}
        <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #C9A84C, transparent)' }}
          />
          <div className="flex items-center justify-between mb-4 relative">
            <div>
              <h2 className="font-display text-lg text-[#F5F0E8]">Réservations par statut</h2>
              <p className="text-xs text-[#B9B2A8]">Répartition globale</p>
            </div>
            <div className="w-9 h-9 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-[#C9A84C]" />
            </div>
          </div>

          {!analyticsLoaded ? (
            <div className="h-56 flex items-center justify-center">
              <div className="w-32 h-32 rounded-full border-4 border-white/10 border-t-[#C9A84C] animate-spin" />
            </div>
          ) : bookingsByStatus.length > 0 ? (
            <>
              <div className="h-60 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {Object.entries(STATUS_GRADIENTS).map(([key, [from, to]]) => (
                        <linearGradient
                          key={key}
                          id={`pie-${key}`}
                          x1="0"
                          y1="0"
                          x2="1"
                          y2="1"
                        >
                          <stop offset="0%" stopColor={from} />
                          <stop offset="100%" stopColor={to} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={bookingsByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      dataKey="value"
                      labelLine={false}
                      label={renderPieLabel}
                      animationBegin={200}
                      animationDuration={900}
                    >
                      {bookingsByStatus.map((entry) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={`url(#pie-${entry.name})`}
                          stroke="rgba(11,11,12,0.9)"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center total */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="font-display text-2xl text-[#F5F0E8] tabular-nums">
                    <CountUp value={bookingsByStatus.reduce((s, b) => s + b.value, 0)} />
                  </div>
                  <div className="text-[10px] text-[#B9B2A8] uppercase tracking-wider">Total</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {bookingsByStatus.map((entry) => (
                  <div
                    key={entry.name}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs"
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: STATUS_COLORS[entry.name] || '#B9B2A8' }}
                    />
                    <span className="text-[#F5F0E8]">{getStatusLabel(entry.name)}</span>
                    <span className="text-[#C9A84C] font-bold">{entry.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-[#B9B2A8] py-12 text-center">Aucune donnée disponible</p>
          )}
        </div>

        {/* Bar Chart - last 7 days */}
        <div className="lg:col-span-3 bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #22c55e, transparent)' }}
          />
          <div className="flex items-center justify-between mb-4 relative">
            <div>
              <h2 className="font-display text-lg text-[#F5F0E8]">Réservations - 7 derniers jours</h2>
              <p className="text-xs text-[#B9B2A8]">Activité récente</p>
            </div>
            <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-green-400" />
            </div>
          </div>

          {!analyticsLoaded ? (
            <div className="h-60 animate-pulse bg-white/5 rounded-lg" />
          ) : (
            <div className="h-60 relative">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={weeklyBookings}
                  margin={{ top: 10, right: 0, bottom: 0, left: -20 }}
                >
                  <defs>
                    <linearGradient id="bar-gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C9A84C" stopOpacity={1} />
                      <stop offset="100%" stopColor="#C9A84C" stopOpacity={0.3} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: '#B9B2A8', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#B9B2A8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<CustomBarTooltip />}
                    cursor={{ fill: 'rgba(201,168,76,0.08)' }}
                  />
                  <Bar
                    dataKey="count"
                    fill="url(#bar-gradient)"
                    radius={[6, 6, 0, 0]}
                    animationDuration={900}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </motion.div>

      {/* Recent bookings */}
      <motion.div
        variants={item}
        className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-display text-lg text-[#F5F0E8]">Réservations récentes</h2>
            <p className="text-xs text-[#B9B2A8]">Les dernières inscriptions</p>
          </div>
          <Link
            to="/admin/bookings"
            className="group text-xs text-[#C9A84C] hover:text-[#FFD97A] flex items-center gap-1 transition-colors"
          >
            Voir tout
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {!analyticsLoaded ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : recentBookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-[10px] text-[#6B645A] uppercase tracking-[0.15em] font-semibold pb-3 pr-4">
                    Nom
                  </th>
                  <th className="text-left text-[10px] text-[#6B645A] uppercase tracking-[0.15em] font-semibold pb-3 pr-4">
                    Événement
                  </th>
                  <th className="text-left text-[10px] text-[#6B645A] uppercase tracking-[0.15em] font-semibold pb-3 pr-4">
                    Pers.
                  </th>
                  <th className="text-left text-[10px] text-[#6B645A] uppercase tracking-[0.15em] font-semibold pb-3">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {recentBookings.map((booking, idx) => (
                    <motion.tr
                      key={booking._id || idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.06, duration: 0.3 }}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors"
                    >
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A84C]/30 to-[#C9A84C]/10 flex items-center justify-center text-xs font-bold text-[#C9A84C] flex-shrink-0">
                            {(booking.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <span className="text-[#F5F0E8] font-medium">{booking.name}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-[#B9B2A8]">{booking.event}</td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1 text-[#F5F0E8]">
                          <Users className="w-3 h-3 text-[#6B645A]" />
                          {booking.guests}
                        </span>
                      </td>
                      <td className="py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border font-medium ${getStatusColor(
                            booking.status
                          )}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(booking.status)}`} />
                          {getStatusLabel(booking.status)}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[#B9B2A8] py-8 text-center">Aucune réservation pour le moment</p>
        )}
      </motion.div>

      {/* Quick actions */}
      <motion.div variants={item} className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-display text-lg text-[#F5F0E8]">Actions rapides</h2>
            <p className="text-xs text-[#B9B2A8]">Accédez rapidement aux fonctions clés</p>
          </div>
          <Sparkles className="w-5 h-5 text-[#C9A84C]" />
        </div>

        <motion.div
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.05 } },
          }}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {quickActions.map((action) => (
            <motion.div
              key={action.path}
              variants={item}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <Link
                to={action.path}
                className="group relative flex items-center gap-4 p-4 bg-gradient-to-br from-white/5 to-white/[0.02] hover:from-white/10 hover:to-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-all overflow-hidden"
              >
                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{
                    background: `radial-gradient(circle at 20% 50%, ${action.color}22, transparent 60%)`,
                  }}
                />

                <div
                  className={`relative w-11 h-11 rounded-xl bg-gradient-to-br ${action.grad} flex items-center justify-center border border-white/10 flex-shrink-0`}
                  style={{
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 12px -4px ${action.color}44`,
                  }}
                >
                  <action.icon className="w-5 h-5" style={{ color: action.color }} />
                </div>

                <div className="relative flex-1 min-w-0">
                  <div className="text-sm text-[#F5F0E8] font-medium truncate">{action.title}</div>
                  <div className="text-xs text-[#B9B2A8] truncate">{action.desc}</div>
                </div>

                <ArrowRight className="relative w-4 h-4 text-[#6B645A] group-hover:text-[#C9A84C] group-hover:translate-x-1 transition-all flex-shrink-0" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default AdminDashboardPage;
